// src/App.js
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import { ApolloProvider } from "@apollo/client";
import ChangePasswordForm from "./scenes/users/changepassword";
import Dashboard from "./scenes/dashboard";
import Modules from "./scenes/RoleManagement/Modules";
import Rights from "./scenes/RoleManagement/Rights";
import RoleGroups from "./scenes/RoleManagement/RoleGroup";
import Role from "./scenes/RoleManagement/Roles";
import AssignRight from "./scenes/RoleManagement/Roles/AssignRoleRights";
import Users from "./scenes/users";
import UsersForm from "./scenes/users/users-form";

import {
  createRoleClient,
  createSetupClient,
  createUsersClient,
} from "./config";

import withLayout from "./components/HOCApp";
import BarChart from "./components/BarChart";
import PersonDetails from "./scenes/Profile";
import ForgotPasswordForm from "./scenes/users/ForgotPassword";
import SubRightTable from "./scenes/RoleManagement/SubRight";
import AccountAndFinanceModule from "./scenes/AccountsAndFinanceManagement/Accounts";
import CompanyManagementModule from "./scenes/CompanyManagement/CompanyManagement";
import ChartOfAccounts from "./scenes/AccountsAndFinanceManagement/chartofaccounts";
import McbSwitch from "./scenes/login/mcbswitch";

import Setups from "./scenes/SetupManagement/LeadSetups";
import Expense from "./scenes/Expenses/components/expenses";

import LoginSide from "./scenes/login/LoginPage";
import OTPVerification from "./scenes/login/OTPVerification"; // adjust path if different
import AuthHandler from "./scenes/login/authHandler"; // adjust path if different
import FixedExpenses from "./scenes/Expenses/components/FixedExpenses";

import AdhocMain from "./scenes/adhoc/adhoc";
import MajorClassification from "./scenes/Reports/MajorClassification";
import MinorClassification from "./scenes/Reports/MinorClassification";
import ReportTypes from "./scenes/Reports/ReportTypes";
import ReportBrowser from "./scenes/Reports/ReportBrowser";
import LandingPage from "./scenes/global/landingpage";
import CustomersJson from "./scenes/Customers/customers/CustomersJson";
import CollateralValuationTable from "./scenes/Customers/CollateralValuation";
import GroupsJson from "./scenes/Customers/groups/GroupsJson";
import ProductsTable from "./scenes/SetupManagement/Products";
import DashboardMessagesTable from "./scenes/SetupManagement/DashboardMessages";
import ProductChargesAccountTable from "./scenes/SetupManagement/ProductChargesAccount";
import ProductChargesTable from "./scenes/SetupManagement/ProductCharge";
import HorizontalTab from "./scenes/Customers/CustomerHorizontalTab";
import BookLoan from "./scenes/LoanManagement/BookLoan";
import ROLoanAppraisal from "./scenes/LoanManagement/ApprovalTable";
import SuspendedTransactionsTable from "./scenes/AccountsAndFinanceManagement/Tables/SuspendedTransactions";
import PullTransactionsTable from "./scenes/AccountsAndFinanceManagement/Tables/PullTransactions";
import BookGroupLoan from "./scenes/GroupLoanManagement/BookLoan";
import GroupDashboard from "./scenes/dashboard/groupDashboard";
import InterestPaymentMethodsTable from "./scenes/SetupManagement/InterestPaymentMethods";
import ReconFileUpload from "./scenes/AccountsAndFinanceManagement/Tables/ManualRecon";
import CollectionAnalysis from "./scenes/dashboard/collectionAnalysis";
import GroupCollectionAnalysis from "./scenes/dashboard/groupCollectionAnalysis";
import PendingDisbursementsTable from "./scenes/LoanManagement/Sababisha";
import ProcessAuthorizationTable from "./scenes/RoleManagement/ProcessAuthorization";
import CustomerBalanceTable from "./scenes/AccountsAndFinanceManagement/Tables/AccountTransfers";
import TransactionsTable from "./scenes/AccountsAndFinanceManagement/Tables/ManageAccountTransfers";
import AnalyticalDashboard from "./scenes/dashboard/AnalyticalDashboard";
import LoanSettings from "./scenes/LoanManagement/LoanSettings";
import SababishaApprovalTable from "./scenes/LoanManagement/Sababisha/Approve";
import Customer360 from "./scenes/Customers/customer360";
import MapComponent from "./scenes/Maps/mapComponent";
import LoanSearchPage from "./scenes/LoanManagement/LoanSchedule";
import LoanStatement from "./scenes/LoanManagement/LoanStatement";
import TermsAndConditions from "./scenes/DocumentManager/TermsandConditions/terms";
import CompanyFiles from "./scenes/DocumentManager/CompanyFiles/companyFiles";
import DisbursmentAnalysis from "./scenes/dashboard/disbursementDashboard";
import GroupDisbursmentAnalysis from "./scenes/dashboard/groupdisbursmentDashboard";
import RescheduleLoan from "./scenes/LoanManagement/LoanReschedule/ReScheduleLoan";
import RescheduleGroupLoan from "./scenes/LoanManagement/LoanReschedule/RescheduleGroupLoan";
import ExcessAmountRecon from "./scenes/LoanManagement/ExcessAmountRecon";
import ReBookLoanUI from "./scenes/LoanManagement/LoanRebooking/ReBookLoanUI";
import RebookGroupLoan from "./scenes/LoanManagement/LoanRebooking/RebookGroupLoanApplication";
import DeclinedApplications from "./scenes/LoanManagement/DelinedApplications";
import CustomerAwaitingApproval from "./scenes/Customers/customers/CustomerAwaitingApproval";
import ApprovedCustomers from "./scenes/Customers/customers/ApprovedCustomer";
import UnlockUserForm from "./scenes/users/UnlockUser";
import ReconLogs from "./scenes/SetupManagement/Reconlogs/reconlogs";
import LoanScheduleRevamped from "./scenes/LoanManagement/LoanSchedule/loanschedulerevamped";
import CustomerStatementRight from "./scenes/Customers/customers/CustomerStatement";
import CompanyLoanAppraisal from "./scenes/LoanManagement/CompanyApprovalTable";
import BranchLoanAppraisal from "./scenes/LoanManagement/BranchApprovalTable";
import RegionLoanAppraisal from "./scenes/LoanManagement/RegionApprovalTable";
import PerformanceDashboard from "./scenes/dashboard/PerformanceDashboard";
import CustomSMSPage from "./scenes/Adohc/promotions/customSMS";
import TargetPerfomance from "./scenes/Targets";
import FieldOfficerLoanAppraisal from "./scenes/GroupLoanManagement/ApprovalTable";
import BranchGroupLoanAppraisal from "./scenes/GroupLoanManagement/BranchAproval";
import RegionGroupLoanAppraisal from "./scenes/GroupLoanManagement/RegionApproval";
import CompanyGroupLoanAppraisal from "./scenes/GroupLoanManagement/CompanyApproval";
import GroupLoanSchedule from "./scenes/LoanManagement/LoanSchedule/GroupLoanSchedule";
import CollateralSetups from "./scenes/SetupManagement/Collaterateral";
import Guarantor360 from "./scenes/Customers/customers/ManageCustomer/GuarantorDetails/Guarantor360/guarantor360";
import GroupStatementRight from "./scenes/Customers/groups/GroupStatement";
import ApplyLoanPenalty from "./scenes/Penalty Management/ApplyPenalty/Customerloan";
import ApplyGroupPenalty from "./scenes/Penalty Management/ApplyPenalty/GroupLoan";
import ApproveCustomerPenaltyLoan from "./scenes/Penalty Management/PenaltyApprovals/Customer";
import ApproveGroupPenaltyLoan from "./scenes/Penalty Management/PenaltyApprovals/Group";
import BranchRegionMigration from "./scenes/Customers/BranchRegionMigration";
import SuspendedSms from "./scenes/AccountsAndFinanceManagement/Tables/Suspended(SMS)/Index";
import LeaveApplicationsTable from "./scenes/HRManagemenet/LeaveApplications";
import HRSetups from "./scenes/HRManagemenet/setups/tab";
import LeaveApprovalsTable from "./scenes/HRManagemenet/LeaveApprovals";
import StaffClaimsTable from "./scenes/HRManagemenet/StaffClaim";
import StaffClaimsByUserTable from "./scenes/HRManagemenet/StaffClaimByUser";
import ImprestApproversTable from "./scenes/HRManagemenet/setups/ImprestApprovers";
import ImprestApplicationsByUserTable from "./scenes/HRManagemenet/ImprestByUser";
import { imprestApplications } from "./data/Queries/HRmanagement/leavemanagement";
import ImprestSurrenderTable from "./scenes/HRManagemenet/ImprestSurrender/surrendar";
import StaffClaimsLinesTable from "./scenes/HRManagemenet/StaffClaimByUser/staffClaimLines";
import ImprestSurrenderLineTable from "./scenes/HRManagemenet/ImprestSurrender/surrenderline";
import RelieversByApplicationId from "./scenes/HRManagemenet/LeaveApplications/Relievers";
import PettyCashTab from "./scenes/HRManagemenet/PettyCash/tab";
import LoanCalculator from "./scenes/LoanManagement/LoanCalculator";
import ImprestApprovalsTable from "./scenes/HRManagemenet/setups/ImprestApprovers";
import CalendarSetupTable from "./scenes/HRManagemenet/setups/CompanyCalendarSetups";
import LeaveApproversTable from "./scenes/HRManagemenet/setups/Approvers";
import LeavePolicyTable from "./scenes/HRManagemenet/setups/leavePolicy";
import { LoanLimitDialog } from "./scenes/CustomerLoanLimit/SetLoanLimitModel";
import CustomerLoanLimitApprovalTableBranch from "./scenes/CustomerLoanLimit/ApproveCustomerLoanLimitBranch";
import CustomerLoanLimitApprovalTableRegion from "./scenes/CustomerLoanLimit/ApproveCustomerLoanLimitRegion";
import CustomerLoanLimitApprovalTableCompany from "./scenes/CustomerLoanLimit/ApproveCustomerLoanLimitCompany";
import CustomerLoanLimitApprovalTableRO from "./scenes/CustomerLoanLimit/ApproveCustomerLoanLimitRegionRo";
import { TourProvider } from "./context/TourContext";
import { NotificationHubProvider } from "./context/NotificationHubContext";
import CrbMonthlyReportGlobalListener from "./scenes/Reports/ReportBrowser/CrbMonthlyReportGlobalListener";
import ReverseActiveLoan from "./scenes/LoanManagement/ReverseActiveLoan";
import DevModePage from "./scenes/DevMode";
import SearchCode from "./scenes/AccountsAndFinanceManagement/Tables/SearchCode/SearchCode";
import AssetFinanceSetups from "./scenes/SetupManagement/AssetFinanceSetups/AssetFinanceSetups";
import DeviceSetups from "./scenes/SetupManagement/DeviceSetups/DeviceSetups";
import BookAssetLoan from "./scenes/AsssetLoanManagement/BookLoan";
import BookDeviceLoan from "./scenes/DeviceLoanManagement/BookLoan/index";
import DeviceRegistryTable from "./scenes/DeviceLoanManagement/DeviceRegistry";
import FieldOfficerAssetLoanApproval from "./scenes/AsssetLoanManagement/Approvals/FieldOfficerApproval";
import BranchAssetLoanApproval from "./scenes/AsssetLoanManagement/Approvals/BranchApproval";
import RegionAssetLoanApproval from "./scenes/AsssetLoanManagement/Approvals/RegionApproval";
import CompanyAssetLoanApproval from "./scenes/AsssetLoanManagement/Approvals/CompanyApproval";
import FieldOfficerDeviceLoanApproval from "./scenes/DeviceLoanManagement/Approvals/FieldOfficerApproval";
import BranchDeviceLoanApproval from "./scenes/DeviceLoanManagement/Approvals/BranchApproval";
import RegionDeviceLoanApproval from "./scenes/DeviceLoanManagement/Approvals/RegionApproval";
import CompanyDeviceLoanApproval from "./scenes/DeviceLoanManagement/Approvals/CompanyApproval";
import LienManagement from "./scenes/AsssetLoanManagement/LienManagement";
import AssetRecovery from "./scenes/AsssetLoanManagement/AssetRecovery";
import VoluntaryRepossessionTable from "./scenes/AsssetLoanManagement/VoluntaryRepossesion";
import AssetTypeDocumentRequirementTable from "./scenes/SetupManagement/AssetFinanceSetups/AssetTypeDocumentRequirement";
import ApprovedCollateralValuationTable from "./scenes/Customers/ApprovedCollateralValuation";
import ApprovedAssetCustomers from "./scenes/Customers/ApprovedAssetCustomers";
import CustomerChargesPage from "./scenes/Customers/CustomerCharges";
import ManualLoanRepaymentTable from "./scenes/AccountsAndFinanceManagement/Tables/ManualLoanRepayment";
import InterestWaiverTable from "./scenes/AccountsAndFinanceManagement/Tables/InterestWaiver";
import CommissionTable from "./scenes/AccountsAndFinanceManagement/Tables/Commission";
import ApprovedCommissionTable from "./scenes/AccountsAndFinanceManagement/Tables/ApproveCommission";
import DisbursementMethodsTable from "./scenes/SetupManagement/DisbursmentMethods";
import CoOwnerModal from "./scenes/Customers/customers/ManageCustomer/CollateralAsset/CoOwnerModal";
import DirectorsModal from "./scenes/Customers/customers/ManageCustomer/CollateralAsset/DirectorsModal";
import CustomerSettings from "./scenes/Customers/customers/CustomerSettings";
import AssetDashboard from "./scenes/dashboard/assetDashboard.jsx";

import { isAuthenticated as checkAuthenticated, clearAuth } from "./scenes/login/authStorage";
import CompanySettings from "./scenes/CompanyManagement/CompanySettings/index.js";
import Finsights from "./scenes/FinsightsManager/finsights/index.jsx";
import WalletManagement from "./scenes/WalletManagement";
import SababishaAdmin from "./scenes/SababishaAdmin";
import SgwApprovals from "./scenes/WalletManagement/SgwApprovals";

// Paths reachable without a verified session.
const PUBLIC_PATHS = [
  "/",
  "/login",
  "/otp-verification",
  "/auth-handler", // wherever AuthHandler is routed — adjust to match your route below
  "/forgot-password",
  "/unlock",
  "/dev-mode",
];

function App() {
  const userManagementClient = createUsersClient();
  const roleManagementClient = createRoleClient();
  const setupManagementClient = createSetupClient();
  const navigate = useNavigate();
  const location = useLocation();
  const [theme, colorMode] = useMode();

  // Derive auth from the real committed session, not a fake timer.
  const [isAuthenticated, setIsAuthenticated] = useState(checkAuthenticated());

  useEffect(() => {
    const checkTokenExpiration = () => {
      const authed = checkAuthenticated();
      setIsAuthenticated(authed);

      if (!authed && !PUBLIC_PATHS.includes(location.pathname)) {
        clearAuth();
        navigate("/login", { replace: true });
      }
    };

    const interval = setInterval(checkTokenExpiration, 60000);
    checkTokenExpiration(); // run immediately, including on every route change

    return () => clearInterval(interval);
    // location.pathname included so navigating to a protected route without
    // a session is caught right away, not up to 60s later.
  }, [navigate, location.pathname]);

  const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes
  useEffect(() => {
    let inactivityTimer;

    const resetInactivityTimer = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        clearAuth();
        sessionStorage.clear();
        setIsAuthenticated(false);
        navigate("/login");
      }, INACTIVITY_LIMIT);
    };

    const handleActivity = () => resetInactivityTimer();

    window.addEventListener("mousemove", handleActivity);
    window.addEventListener("keydown", handleActivity);
    resetInactivityTimer();

    return () => {
      window.removeEventListener("mousemove", handleActivity);
      window.removeEventListener("keydown", handleActivity);
      if (inactivityTimer) clearTimeout(inactivityTimer);
    };
  }, [navigate]);

  // Removed: the fake setTimeout(() => setIsAuthenticated(true), 3000) effect.
  // Auth state now only ever flips to true via OTPVerification's
  // onVerifySuccess callback, or on mount if a valid session already exists.

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />{" "}
        <TourProvider>
          <ApolloProvider client={userManagementClient}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route
                path="/login"
                element={<LoginSide onLogin={() => setIsAuthenticated(true)} />}
              />
              <Route
                path="/otp-verification"
                element={
                  <OTPVerification onVerifySuccess={() => setIsAuthenticated(true)} />
                }
              />
              <Route path="/auth-handler" element={<AuthHandler />} />
              <Route path="/changepassword" element={<ChangePasswordForm />} />
              <Route path="/forgot-password" element={<ForgotPasswordForm />} />
              <Route path="/MCB-SWITCH" element={<McbSwitch />} />
              <Route path="/unlock" element={<UnlockUserForm />} />
              <Route path="/dev-mode" element={<DevModePage />} />
              {/* Catch-all: anything unmatched and unauthenticated goes to login
                  instead of rendering a blank page. */}
              {!isAuthenticated && (
                <Route path="*" element={<Navigate to="/login" replace />} />
              )}
            </Routes>
          </ApolloProvider>

          {isAuthenticated && (
            <NotificationHubProvider>
              <CrbMonthlyReportGlobalListener />
              <ApolloProvider client={roleManagementClient}>
                <Routes>
                  <Route path="/profile" element={withLayout(PersonDetails)()} />
                  <Route path="/module" element={withLayout(Modules)()} />
                  <Route path="/rights" element={withLayout(Rights)()} />
                  <Route
                    path="/role-group"
                    element={withLayout(RoleGroups)()}
                  />
                  <Route path="/role" element={withLayout(Role)()} />
                  <Route
                    path="/subright"
                    element={withLayout(SubRightTable)()}
                  />
                  <Route
                    path="/role-Rights"
                    element={withLayout(AssignRight)()}
                  />
                  <Route
                    path="/processauthorization"
                    element={withLayout(ProcessAuthorizationTable)()}
                  />
                  <Route
                    path="/role-Rights"
                    element={withLayout(AssignRight)()}
                  />
                  <Route
                    path="/processauthorization"
                    element={withLayout(ProcessAuthorizationTable)()}
                  />
                  {/* Customers -  Groups */}
                  <Route
                    path="/customers"
                    element={withLayout(CustomersJson)()}
                  />
                  <Route
                    path="/collateral-pending-approval"
                    element={withLayout(CollateralValuationTable)()}
                  />
                  <Route
                    path="/customerstatement"
                    element={withLayout(CustomerStatementRight)()}
                  />
                  <Route
                    path="/groupstatement"
                    element={withLayout(GroupStatementRight)()}
                  />
                  <Route path="/groups" element={withLayout(GroupsJson)()} />
                  <Route
                    path="/managegroup"
                    element={withLayout(HorizontalTab)()}
                  />
                  <Route
                    path="/customer-details"
                    element={withLayout(HorizontalTab)()}
                  />
                  <Route
                    path="/customer-360"
                    element={withLayout(Customer360)()}
                  />
                  <Route
                    path="/guarantor-360"
                    element={withLayout(Guarantor360)()}
                  />
                  <Route
                    path="/CoOwners"
                    element={withLayout(CoOwnerModal)()}
                  />
                   <Route
                    path="/directors"
                    element={withLayout(DirectorsModal)()}
                  />

                  <Route path="/maps" element={withLayout(MapComponent)()} />
                  <Route
                    path="/customer-loan-limit-approval-branch"
                    element={withLayout(CustomerLoanLimitApprovalTableBranch)()}
                  />
                  <Route
                    path="/loan-limit-approval-region"
                    element={withLayout(CustomerLoanLimitApprovalTableRegion)()}
                  />
                  <Route
                    path="/customer-loan-limit-approval-company"
                    element={withLayout(
                      CustomerLoanLimitApprovalTableCompany,
                    )()}
                  />
                  <Route
                    path="/loan-limit-approval-ro"
                    element={withLayout(CustomerLoanLimitApprovalTableRO)()}
                  />
                  <Route
                    path="/set-loan-limit"
                    element={withLayout(LoanLimitDialog)()}
                  />
                  <Route
                    path="/customer-migration"
                    element={withLayout(AccountAndFinanceModule)()}
                  />
                  <Route
                    path="/branch-region-migration"
                    element={withLayout(BranchRegionMigration)()}
                  />
                  <Route
                    path="/customer-awaiting-approval"
                    element={withLayout(ApprovedCustomers)()}
                  />
                  <Route
                    path="/customer-approve-disapprove"
                    element={withLayout(CustomerAwaitingApproval)()}
                  />
                  <Route
                    path="/customer-charges"
                    element={withLayout(CustomerChargesPage)()}
                  />
                  <Route
                    path="/customer-group-migration"
                    element={withLayout(AccountAndFinanceModule)()}
                  />
                  {/* <Route path="/opo-setups" element={withLayout(Setups)()} /> */}
                </Routes>
              </ApolloProvider>
              <ApolloProvider client={userManagementClient}>
                <Routes>
                  <Route path="/users" element={withLayout(Users)()} />
                  <Route
                    path="/perfomance-targets"
                    element={withLayout(TargetPerfomance)()}
                  />
                  <Route path="/usersform" element={withLayout(UsersForm)()} />
                </Routes>
              </ApolloProvider>

              <ApolloProvider client={setupManagementClient}>
                <Routes>
                  <Route path="/opo-setups" element={withLayout(Setups)()} />
                  <Route
                    path="/opo-products"
                    element={withLayout(ProductsTable)()}
                  />
                  <Route
                    path="/dashboard-notications"
                    element={withLayout(DashboardMessagesTable)()}
                  />
                  <Route
                    path="/productchargesaccount"
                    element={withLayout(ProductChargesAccountTable)()}
                  />
                  <Route
                    path="/collateral-setups"
                    element={withLayout(CollateralSetups)()}
                  />
                  <Route
                    path="/asset-finance-setups"
                    element={withLayout(AssetFinanceSetups)()}
                  />
                  <Route
                    path="/device-setups"
                    element={withLayout(DeviceSetups)()}
                  />
                  <Route
                    path="/device-lock-setups"
                    element={withLayout(DeviceSetups)()}
                  />
                  <Route
                    path="/productcharges"
                    element={withLayout(ProductChargesTable)()}
                  />
                  <Route
                    path="/document-requirement"
                    element={withLayout(AssetTypeDocumentRequirementTable)()}
                  />
                  <Route
                    path="/interestpaymentmethods"
                    element={withLayout(InterestPaymentMethodsTable)()}
                  />
                  <Route
                    path="/manage-companies"
                    element={withLayout(CompanyManagementModule)()}
                  />
                  <Route
                    path="/chart-of-accounts"
                    element={withLayout(AccountAndFinanceModule)()}
                  />
                  <Route
                    path="/suspended-transactions"
                    element={withLayout(SuspendedTransactionsTable)()}
                  />
                  <Route
                    path="/commissions"
                    element={withLayout(CommissionTable)()}
                  />
                  <Route
                    path="/approved-commissions"
                    element={withLayout(ApprovedCommissionTable)()}
                  />
                  <Route
                    path="/disbursment-method"
                    element={withLayout(DisbursementMethodsTable)()}
                  />
                  <Route
                    path="/pull-transactions"
                    element={withLayout(PullTransactionsTable)()}
                  />
                  <Route
                    path="/suspended-b2b-transactions"
                    element={withLayout(SuspendedSms)()}
                  />
                  <Route
                    path="/recon-fileupload"
                    element={withLayout(ReconFileUpload)()}
                  />
                  <Route
                    path="/account-transfers"
                    element={withLayout(CustomerBalanceTable)()}
                  />
                  <Route
                    path="/manage-account-transfers"
                    element={withLayout(TransactionsTable)()}
                  />

                  <Route path="/expenses" element={withLayout(Expense)()} />
                  <Route path="/adhoc" element={withLayout(AdhocMain)()} />
                  <Route
                    path="/adhocsms"
                    element={withLayout(CustomSMSPage)()}
                  />
                  <Route
                    path="/finsights"
                    element={withLayout(Finsights)()}
                  />

                  <Route
                    path="/terms"
                    element={withLayout(TermsAndConditions)()}
                  />
                  <Route
                    path="/companyFiles"
                    element={withLayout(CompanyFiles)()}
                  />
                  <Route
                    path="/fixed-expenses"
                    element={withLayout(FixedExpenses)()}
                  />
                </Routes>
              </ApolloProvider>

              <Routes>
                {/* Loan Management */}
                <Route path="/book-loan" element={withLayout(BookLoan)()} />
                <Route
                  path="/apply-loan-penalty"
                  element={withLayout(ApplyLoanPenalty)()}
                />
                <Route
                  path="/apply-group-loan-penalty"
                  element={withLayout(ApplyGroupPenalty)()}
                />
                <Route
                  path="/active-loan-reversal"
                  element={withLayout(ReverseActiveLoan)()}
                />
                <Route
                  path="/approve-loan-penalty"
                  element={withLayout(ApproveCustomerPenaltyLoan)()}
                />
                <Route
                  path="/approve-group-penalty"
                  element={withLayout(ApproveGroupPenaltyLoan)()}
                />
                <Route
                  path="/loan-rebooking"
                  element={withLayout(ReBookLoanUI)()}
                />
                <Route
                  path="/group-loan-rebooking"
                  element={withLayout(RebookGroupLoan)()}
                />
                <Route
                  path="/reschedule-loan"
                  element={withLayout(RescheduleLoan)()}
                />
                <Route
                  path="/reschedule-group-loan"
                  element={withLayout(RescheduleGroupLoan)()}
                />
                <Route
                  path="/declined-loan"
                  element={withLayout(DeclinedApplications)()}
                />

                <Route
                  path="/field-officer-approval"
                  element={withLayout(ROLoanAppraisal)()}
                />
                <Route
                  path="/region-approval"
                  element={withLayout(RegionLoanAppraisal)()}
                />
                <Route
                  path="/branch-approval"
                  element={withLayout(BranchLoanAppraisal)()}
                />
                <Route
                  path="/company-approval"
                  element={withLayout(CompanyLoanAppraisal)()}
                />
                <Route
                  path="/sababisha"
                  element={withLayout(PendingDisbursementsTable)()}
                />
                <Route
                  path="/sababisha-approval"
                  element={withLayout(SababishaApprovalTable)()}
                />
                {/*Group Loan Management */}
                <Route
                  path="/book-group-loan"
                  element={withLayout(BookGroupLoan)()}
                />
                <Route
                  path="/group-field-officer-approval"
                  element={withLayout(FieldOfficerLoanAppraisal)()}
                />
                <Route
                  path="/loan-calculator"
                  element={withLayout(LoanCalculator)()}
                />
                <Route
                  path="/group-region-approval"
                  element={withLayout(RegionGroupLoanAppraisal)()}
                />
                <Route
                  path="/group-branch-approval"
                  element={withLayout(BranchGroupLoanAppraisal)()}
                />
                <Route
                  path="/group-company-approval"
                  element={withLayout(CompanyGroupLoanAppraisal)()}
                />
                <Route
                  path="/loan-settings"
                  element={withLayout(LoanSettings)()}
                />
                <Route
                  path="/customer-settings"
                  element={withLayout(CustomerSettings)()}
                />
                <Route
                  path="/company-settings"
                  element={withLayout(CompanySettings)()}
                />
                <Route
                  path="/wallet-management"
                  element={withLayout(WalletManagement)()}
                />
                <Route
                  path="/company-wallet"
                  element={withLayout(WalletManagement)()}
                />
                <Route
                  path="/sababisha-wallet-admin"
                  element={withLayout(SababishaAdmin)()}
                />
                <Route
                  path="/sgw-approvals"
                  element={withLayout(SgwApprovals)()}
                />
                <Route
                  path="/sababisha-admin"
                  element={withLayout(SababishaAdmin)()}
                />
                <Route
                  path="/loan-schedules"
                  element={withLayout(LoanScheduleRevamped)()}
                />
                <Route
                  path="/loan-statement"
                  element={withLayout(LoanStatement)()}
                />
                <Route
                  path="/group-loan-schedule"
                  element={withLayout(GroupLoanSchedule)()}
                />
                {/* Reports */}
                <Route
                  path="/reports-major-classification"
                  element={withLayout(MajorClassification)()}
                />
                <Route
                  path="/reports-minor-classification"
                  element={withLayout(MinorClassification)()}
                />
                <Route
                  path="/report-types"
                  element={withLayout(ReportTypes)()}
                />
                <Route
                  path="/report-browser"
                  element={withLayout(ReportBrowser)()}
                />
                <Route path="/bar" element={withLayout(BarChart)()} />
                <Route
                  path="/super-admin-dashboard"
                  element={withLayout(Dashboard)()}
                />
                <Route
                  path="/asset-admin-dashboard"
                  element={withLayout(AssetDashboard)()}
                />
                <Route path="/recon-logs" element={withLayout(ReconLogs)()} />
                <Route
                  path="/group-dashboard"
                  element={withLayout(GroupDashboard)()}
                />
                <Route
                  path="/collection-dashboard"
                  element={withLayout(CollectionAnalysis)()}
                />
                <Route
                  path="/group-collection-dashboard"
                  element={withLayout(GroupCollectionAnalysis)()}
                />
                <Route
                  path="/analytical-dashboard"
                  element={withLayout(AnalyticalDashboard)()}
                />
                <Route
                  path="/disbursment-dashboard"
                  element={withLayout(DisbursmentAnalysis)()}
                />
                <Route
                  path="/performance-dashboard"
                  element={withLayout(PerformanceDashboard)()}
                />
                <Route
                  path="/group-disbursment-dashboard"
                  element={withLayout(GroupDisbursmentAnalysis)()}
                />
                {/* <Route path="/adhoc" element={withLayout(AdhocMain)()} /> */}
              </Routes>
              {/* HR management */}
              <Routes>
                <Route
                  path="/leave-approvals"
                  element={withLayout(LeaveApprovalsTable)()}
                />
                <Route
                  path="/leave-applications"
                  element={withLayout(HorizontalTab)()}
                />
                <Route
                  path="/staff-claims"
                  element={withLayout(HorizontalTab)()}
                />
                <Route
                  path="/staff-claim-approvals"
                  element={withLayout(StaffClaimsTable)()}
                />

                <Route
                  path="/Imprest-Surrender"
                  element={withLayout(ImprestSurrenderTable)()}
                />
                <Route
                  path="/imprest-approvals"
                  element={withLayout(ImprestApprovalsTable)()}
                />
                <Route
                  path="/imprest-applications"
                  element={withLayout(HorizontalTab)()}
                />
                <Route
                  path="/staff-claim-Lines"
                  element={withLayout(StaffClaimsLinesTable)()}
                />
                <Route
                  path="/relievers"
                  element={withLayout(RelieversByApplicationId)()}
                />
                <Route
                  path="/petty-cash"
                  element={withLayout(PettyCashTab)()}
                />
                <Route
                  path="/imprest-Surrender-Lines"
                  element={withLayout(ImprestSurrenderLineTable)()}
                />
                <Route path="/hr-setups" element={withLayout(HRSetups)()} />
                <Route
                  path="/leave-approvers"
                  element={withLayout(LeaveApproversTable)()}
                />
                <Route
                  path="/calendar-setups"
                  element={withLayout(CalendarSetupTable)()}
                />
                <Route
                  path="/imprest-approvers"
                  element={withLayout(ImprestApproversTable)()}
                />
                <Route
                  path="/leave-policy"
                  element={withLayout(LeavePolicyTable)()}
                />
                <Route
                  path="/search-mpesa-code"
                  element={withLayout(SearchCode)()}
                />
                <Route
                  path="/book-asset-loan"
                  element={withLayout(BookAssetLoan)()}
                />
                <Route
                  path="/book-device-loan"
                  element={withLayout(BookDeviceLoan)()}
                />
                <Route
                  path="/device-registry"
                  element={withLayout(DeviceRegistryTable)()}
                />
                <Route
                  path="/asset-field-officer-approval"
                  element={withLayout(FieldOfficerAssetLoanApproval)()}
                />
                <Route
                  path="/asset-branch-approval"
                  element={withLayout(BranchAssetLoanApproval)()}
                />
                <Route
                  path="/asset-region-approval"
                  element={withLayout(RegionAssetLoanApproval)()}
                />
                <Route
                  path="/asset-company-approval"
                  element={withLayout(CompanyAssetLoanApproval)()}
                />
                <Route
                  path="/device-field-officer-approval"
                  element={withLayout(FieldOfficerDeviceLoanApproval)()}
                />
                <Route
                  path="/device-branch-approval"
                  element={withLayout(BranchDeviceLoanApproval)()}
                />
                <Route
                  path="/device-region-approval"
                  element={withLayout(RegionDeviceLoanApproval)()}
                />
                <Route
                  path="/device-company-approval"
                  element={withLayout(CompanyDeviceLoanApproval)()}
                />
                <Route
                  path="/approved-collateral-assets"
                  element={withLayout(ApprovedCollateralValuationTable)()}
                />
                <Route
                  path="/approved-asset-customers"
                  element={withLayout(ApprovedAssetCustomers)()}
                />
                <Route
                  path="/lien-management"
                  element={withLayout(LienManagement)()}
                />
                <Route
                  path="/collateral-asset-recovery"
                  element={withLayout(AssetRecovery)()}
                />
                <Route
                  path="/manual-loan-repayment"
                  element={withLayout(ManualLoanRepaymentTable)()}
                />
                <Route
                  path="/interest-waiver"
                  element={withLayout(InterestWaiverTable)()}
                />
                <Route
                  path="/voluntary-repossession"
                  element={withLayout(VoluntaryRepossessionTable)()}
                />
              </Routes>
            </NotificationHubProvider>
          )}
        </TourProvider>
      </ThemeProvider>

      {/* </Provider> */}
    </ColorModeContext.Provider>
  );
}

export default App;
